import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { DEMAND_TYPE_CONFIG } from './useSupportInbox';
import type { DemandType } from '@/types/support.types';

interface CreateSiblingSessionPayload {
  contactId: string;
  demandType: DemandType;
  assignedTo?: string;
  priority?: string;
  queue?: string;
}

export function useCreateSiblingSession() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (payload: CreateSiblingSessionPayload): Promise<string> => {
      const { data, error } = await supabase.rpc('create_sibling_session', {
        p_contact_id: payload.contactId,
        p_demand_type: payload.demandType,
        p_assigned_to: payload.assignedTo || null,
        p_priority: payload.priority || 'normal',
        p_queue: payload.queue || 'general',
      });

      if (error) {
        // Check for unique constraint violation
        if (error.code === '23505' || error.message?.includes('uq_active_session_per_contact_demand')) {
          const typeLabel = DEMAND_TYPE_CONFIG[payload.demandType]?.label || payload.demandType;
          throw new Error(`Já existe uma sessão ativa de ${typeLabel} para este contato`);
        }
        throw error;
      }

      return data as string;
    },
    onSuccess: (sessionId, payload) => {
      const typeLabel = DEMAND_TYPE_CONFIG[payload.demandType]?.label || payload.demandType;
      toast.success(`Nova sessão de ${typeLabel} criada`);
      
      // Invalidate inbox queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['support-sessions'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['support-contact-sessions-full'] });
      queryClient.invalidateQueries({ queryKey: ['contact-sessions'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao criar sessão');
    },
  });

  return {
    createSiblingSession: mutation.mutateAsync,
    isCreating: mutation.isPending,
  };
}
